import * as logClient from "@classdojo/log-client";
import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import { translate } from "@classdojo/web/utils/translate";
import { useState, useEffect, useCallback, useMemo } from "react";
import ShufflingAvatars from "./ShufflingAvatars";
import { IdeasShuffleIcon } from "components/IdeasIcons";
import { Person } from "components/PersonListItem";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { randomIndex, generateLoadingAnimation } from "utils";
import shuffle from "lodash/shuffle";

type WhoseTurnCardContentProps = {
  persons: Person[];
};

export default function WhoseTurnCardContent({ persons }: WhoseTurnCardContentProps) {
  const [shuffledPersons, setShuffledPersons] = useState<Person[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [shuffling, setShuffling] = useState(true);
  const [loadingAvatar, onAnimationStateChangeAvatar] = useState<string | undefined>();
  const [showFirstShuffledPerson, setShowFirstShuffledPerson] = useState(true);

  const shufflePeople = useCallback(() => {
    let currentAvatar = loadingAvatar;
    if (persons.length > 0) {
      setShuffledPersons(shuffle(persons));
      setCurrentIndex(0);
      generateLoadingAnimation({
        numSegments: 6,
        endIntervalDuration: 100,
        startIntervalDuration: 300,
        onAnimationStateChange: (shuffling: boolean) => setShuffling(shuffling),
        onAnimationTick: () => {
          const filteredAllPersons = persons.filter((p) => p.avatar !== currentAvatar);
          // If there was only one person left, it's their turn.
          currentAvatar = filteredAllPersons[randomIndex(filteredAllPersons)]?.avatar ?? currentAvatar;
          onAnimationStateChangeAvatar(currentAvatar);
        },
      })();
    }
  }, [persons, setShuffledPersons, setCurrentIndex, onAnimationStateChangeAvatar, setShuffling, loadingAvatar]);

  const blinkFirstShuffledPerson = useCallback(() => {
    /*
      NOTE:
      - we have to declare a local variable, show, here as this function definition
        is set once and so the value for showFirstShuffledPerson will not be updated
        during the intveral
      - more context here: https://docs.google.com/document/d/1ozjk2PLg4yEOD9C4B7Emk9Y-8JBrj1M_GWYU1K4_3PE/edit?usp=sharing
    */
    let show = showFirstShuffledPerson;
    const finishAudioNode = document.querySelector<HTMLAudioElement>("#finish-audio");
    setTimeout(() => {
      if (finishAudioNode) {
        finishAudioNode.play().catch(() => {
          logClient.sendMetrics([
            {
              type: "increment",
              value: 1,
              metricName: "toolkit.media.audio.play.error",
            },
          ]);
        });
      }
      let blinkInterval: number | undefined = window.setInterval(() => {
        show = !show;
        setShowFirstShuffledPerson(show);
      }, 200);
      setTimeout(() => {
        setShowFirstShuffledPerson(true);
        window.clearInterval(blinkInterval);
        blinkInterval = undefined;
      }, 800);
    }, 3000);
    // animation lasts for 3 seconds, so want to wait for that before displaying blinking text
  }, [setShowFirstShuffledPerson, showFirstShuffledPerson]);

  const reshuffle = useCallback(() => {
    const rollingAudioNode = document.querySelector<HTMLAudioElement>("#rolling-audio");
    if (rollingAudioNode) {
      rollingAudioNode.play().catch(() => {
        logClient.sendMetrics([
          {
            type: "increment",
            value: 1,
            metricName: "toolkit.media.audio.play.error",
          },
        ]);
      });
    }
    shufflePeople();
    blinkFirstShuffledPerson();
  }, [shufflePeople, blinkFirstShuffledPerson]);

  // bulk ignore existing useEffects
  // eslint-disable-next-line @web-monorepo/no-useEffect
  useEffect(() => {
    reshuffle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persons]);

  const moveToPreviousPerson = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex, setCurrentIndex]);

  const moveToNextPerson = useCallback(() => {
    setCurrentIndex(currentIndex + 1);
  }, [currentIndex, setCurrentIndex]);

  const clickShuffle = useCallback(() => {
    logParentToolkitEvent({ eventName: `whose_turn.shuffle.tap` });
    reshuffle();
  }, [reshuffle]);

  const currentIndexWithinRange = useMemo(() => currentIndex % shuffledPersons.length, [currentIndex, shuffledPersons]);

  const currentPerson = useMemo(
    () => shuffledPersons[currentIndexWithinRange],
    [shuffledPersons, currentIndexWithinRange],
  );

  const previousPerson = useMemo(
    () => shuffledPersons[(currentIndex - 1) % shuffledPersons.length],
    [shuffledPersons, currentIndex],
  );

  const nextPerson = useMemo(
    () => shuffledPersons[(currentIndex + 1) % shuffledPersons.length],
    [shuffledPersons, currentIndex],
  );

  const color = useMemo(
    () => (showFirstShuffledPerson ? "dt_content_primary" : "dt_content_light"),
    [showFirstShuffledPerson],
  );

  if (shuffling) {
    return <ShufflingAvatars avatar={loadingAvatar} />;
  }

  return (
    <div sx={{ position: "relative" }}>
      <div sx={{ fontWeight: 900, fontSize: 24, color }}>
        {translate("dojo.beyond:toolkit.whose_turn.current_turn", { firstName: currentPerson.firstName })}
      </div>
      <div
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingTop: "dt_m",
          paddingBottom: "dt_m",
          position: "relative",
          height: 180,
        }}
      >
        {currentIndex > 0 && <img src={previousPerson.avatar} sx={secondaryAvatarStyle(false)} />}
        <img src={currentPerson.avatar} sx={{ maxHeight: 128, position: "absolute" }} />
        <img src={nextPerson.avatar} sx={secondaryAvatarStyle(true)} />
      </div>
      <div
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "dt_l", position: "relative" }}
      >
        {currentIndex > 0 && (
          <img
            width="25px"
            sx={{ marginLeft: RAW_cssValue(-120), position: "absolute" }}
            src="/static/images/previous_icon.png"
            onClick={() => {
              logParentToolkitEvent({ eventName: `whose_turn.previous.tap` });
              moveToPreviousPerson();
            }}
          />
        )}
        <div sx={{ position: "absolute" }}>
          <div
            onClick={clickShuffle}
            sx={{
              height: 64,
              width: 64,
              borderRadius: "dt_radius_round",
              border: "dt_card",
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
              position: "relative",
            }}
          >
            <IdeasShuffleIcon size="l" />
          </div>
        </div>
        <img
          width="25px"
          sx={{ transform: "scaleX(-1)", marginLeft: RAW_cssValue(120), position: "absolute" }}
          src="/static/images/previous_icon.png"
          onClick={() => {
            logParentToolkitEvent({ eventName: `whose_turn.next.tap` });
            moveToNextPerson();
          }}
        />
      </div>
    </div>
  );
}

const secondaryAvatarStyle = (next = true): ThemeUIStyleObject => ({
  maxHeight: 80,
  opacity: 0.4,
  marginLeft: next ? RAW_cssValue(100) : RAW_cssValue(-100),
  position: "absolute",
});
